<template>
  <div>
    <!-- User Level Overview -->
    <div class="grid grid-cols-3 gap-3 mb-4">
      <div class="bg-gray-50 border rounded p-3">
        <div class="text-xs text-gray-500 mb-1">Stored Current Level</div>
        <div class="text-lg font-bold">{{ report.storedCurrentLevel }}</div>
      </div>
      <div class="bg-gray-50 border rounded p-3">
        <div class="text-xs text-gray-500 mb-1">Derived Current Level</div>
        <div
          class="text-lg font-bold"
          :class="report.storedCurrentLevel !== report.derivedCurrentLevel ? 'text-red-600' : 'text-green-600'"
        >
          {{ report.derivedCurrentLevel }}
        </div>
      </div>
      <div class="bg-gray-50 border rounded p-3">
        <div class="text-xs text-gray-500 mb-1">Legacy Level</div>
        <div
          class="text-lg font-bold"
          :class="report.legacyCurrentLevel !== null && report.legacyCurrentLevel !== report.storedCurrentLevel ? 'text-orange-600' : 'text-gray-700'"
        >
          {{ report.legacyCurrentLevel ?? 'N/A' }}
        </div>
      </div>
    </div>

    <!-- Critical Anomalies — animated warning banner -->
    <div v-if="criticalAnomalies.length > 0" class="mb-4">
      <div class="space-y-2">
        <div
          v-for="(anomaly, idx) in criticalAnomalies"
          :key="'critical-' + idx"
          class="anomaly-critical-banner rounded-lg p-4 flex items-start justify-between gap-3"
        >
          <div class="flex items-start gap-3">
            <i class="pi pi-exclamation-circle text-2xl anomaly-critical-icon mt-0.5" />
            <div>
              <span class="text-xs font-bold uppercase tracking-wide">Critical — Level Mismatch</span>
              <div class="text-sm font-medium mt-1">{{ anomaly.description }}</div>
              <div class="text-xs mt-1 opacity-80">
                Stored Level: <strong>{{ anomaly.stored }}</strong> — Actual Level: <strong>{{ anomaly.expected }}</strong>
              </div>
            </div>
          </div>
          <button
            class="px-4 py-2 text-xs font-bold bg-white text-red-700 rounded-lg hover:bg-red-50 shadow-sm border border-red-300 shrink-0"
            @click="emitRepair('reset_current_level')"
          >
            Reset Current Level
          </button>
        </div>
      </div>
    </div>

    <!-- Global Anomalies (level-independent, non-critical) -->
    <div v-if="nonCriticalGlobalAnomalies.length > 0" class="mb-4">
      <h4 class="text-sm font-semibold mb-2 text-red-700">Global Anomalies</h4>
      <div class="space-y-2">
        <div
          v-for="(anomaly, idx) in nonCriticalGlobalAnomalies"
          :key="'global-' + idx"
          :class="[
            'rounded p-3 flex items-start justify-between gap-3',
            anomaly.severity === 'hint'
              ? 'bg-amber-50 border border-amber-200'
              : 'bg-red-50 border border-red-200',
          ]"
        >
          <div>
            <span
              :class="[
                'text-xs font-medium px-1.5 py-0.5 rounded',
                anomaly.severity === 'hint'
                  ? 'bg-amber-100 text-amber-800'
                  : 'bg-red-100 text-red-800',
              ]"
            >
              {{ anomaly.severity === 'hint' ? 'Hint' : formatAnomalyType(anomaly.type) }}
            </span>
            <div class="text-sm text-gray-700 mt-1">{{ anomaly.description }}</div>
          </div>
          <div class="flex gap-2 shrink-0">
            <button
              v-if="anomaly.type === 'MISSING_LEVEL_PROGRESS'"
              class="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
              @click="emitRepair('assign_missing_levels')"
            >
              Assign Missing Levels
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Per-Level Breakdown -->
    <div class="space-y-3">
      <div
        v-for="level in report.levels"
        :key="level.level"
        class="border rounded"
        :class="levelBorderClass(level)"
      >
        <!-- Level Header -->
        <div
          class="flex items-center justify-between px-4 py-2 cursor-pointer"
          :class="levelHeaderClass(level)"
          @click="toggleLevel(level.level)"
        >
          <div class="flex items-center gap-3">
            <i
              :class="expandedLevels[level.level] ? 'pi pi-chevron-down' : 'pi pi-chevron-right'"
              class="text-xs text-gray-400"
            />
            <span class="font-medium text-sm">Level {{ level.level }}: {{ level.title }}</span>
            <span
              :class="[
                'text-xs px-2 py-0.5 rounded-full',
                level.completed ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700',
              ]"
            >
              {{ level.completed ? 'Completed' : 'In Progress' }}
            </span>
          </div>
          <div class="flex items-center gap-3">
            <span class="text-xs text-gray-500">{{ level.currentXP }}/{{ level.totalXP }} XP</span>
            <span
              v-if="levelErrorCount(level) > 0"
              class="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full"
            >
              {{ levelErrorCount(level) }} anomalies
            </span>
            <span
              v-if="levelHintCount(level) > 0"
              class="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full"
            >
              {{ levelHintCount(level) }} hints
            </span>
          </div>
        </div>

        <!-- Level Details (expanded) -->
        <div v-if="expandedLevels[level.level]" class="px-4 py-3 border-t">
          <!-- Anomalies for this level -->
          <div v-if="level.anomalies.length > 0" class="mb-4">
            <div class="text-xs font-semibold text-red-700 mb-2">Anomalies</div>
            <div class="space-y-2">
              <div
                v-for="(anomaly, idx) in level.anomalies"
                :key="idx"
                :class="[
                  'rounded p-2 flex items-start justify-between gap-2',
                  anomaly.severity === 'critical'
                    ? 'anomaly-critical-banner'
                    : anomaly.severity === 'hint'
                      ? 'bg-amber-50 border border-amber-200'
                      : 'bg-red-50 border border-red-200',
                ]"
              >
                <div class="min-w-0">
                  <span
                    :class="[
                      'text-xs font-medium px-1.5 py-0.5 rounded',
                      anomaly.severity === 'critical'
                        ? 'bg-red-700 text-white font-bold'
                        : anomaly.severity === 'hint'
                          ? 'bg-amber-100 text-amber-800'
                          : 'bg-red-100 text-red-800',
                    ]"
                  >
                    {{ anomaly.severity === 'critical' ? 'CRITICAL' : anomaly.severity === 'hint' ? 'Hint' : formatAnomalyType(anomaly.type) }}
                  </span>
                  <div class="text-xs mt-1" :class="anomaly.severity === 'critical' ? 'font-medium' : 'text-gray-700'">{{ anomaly.description }}</div>
                </div>
                <div class="flex gap-1 shrink-0">
                  <button
                    v-if="anomaly.type === 'GOAL_MISMATCH'"
                    class="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
                    @click="emitRepair('sync_goals', level.level)"
                  >
                    Sync Goals
                  </button>
                  <button
                    v-if="anomaly.type === 'GOAL_MISMATCH' && anomaly.stored && !anomaly.expected"
                    class="px-2 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700"
                    @click="emitRepair('delete_goal', level.level, anomaly.goalId)"
                  >
                    Delete Goal
                  </button>
                  <button
                    v-if="anomaly.type === 'XP_MISMATCH' || anomaly.type === 'COMPLETION_MISMATCH'"
                    class="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
                    @click="emitRepair('recalculate')"
                  >
                    Recalculate
                  </button>
                  <button
                    v-if="anomaly.type === 'GOAL_PROGRESS_BEHIND'"
                    class="px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700"
                    @click="emitRepair('fix_goal_progress', level.level, anomaly.goalId)"
                  >
                    Fix Goal Progress
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Goals Comparison -->
          <div class="text-xs font-semibold text-gray-600 mb-2">
            Goals ({{ level.userGoals.length }} user / {{ level.templateGoals.length }} template)
          </div>
          <table class="w-full text-xs border-collapse">
            <thead>
              <tr class="bg-gray-100">
                <th class="text-left p-1.5 border">Goal ID</th>
                <th class="text-left p-1.5 border">Title</th>
                <th class="text-left p-1.5 border">Type</th>
                <th class="text-center p-1.5 border">Progress</th>
                <th class="text-center p-1.5 border">Required</th>
                <th class="text-center p-1.5 border">XP</th>
                <th class="text-center p-1.5 border">Completed</th>
                <th class="text-center p-1.5 border">In Template</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="goal in mergedGoals(level)"
                :key="goal.id"
                :class="goal.mismatch ? 'bg-red-50' : ''"
              >
                <td class="p-1.5 border font-mono">{{ goal.id }}</td>
                <td class="p-1.5 border">{{ goalTitle(goal) }}</td>
                <td class="p-1.5 border">{{ goal.type || '-' }}</td>
                <td class="text-center p-1.5 border">{{ goal.progress ?? '-' }}</td>
                <td class="text-center p-1.5 border">{{ goal.requiredCompletions ?? '-' }}</td>
                <td class="text-center p-1.5 border">{{ goal.xp ?? '-' }}</td>
                <td class="text-center p-1.5 border">
                  <i
                    v-if="goal.completed !== undefined"
                    :class="goal.completed ? 'pi pi-check text-green-600' : 'pi pi-times text-red-500'"
                  />
                  <span v-else>-</span>
                </td>
                <td class="text-center p-1.5 border">
                  <i
                    :class="goal.inTemplate ? 'pi pi-check text-green-600' : 'pi pi-times text-red-500'"
                  />
                </td>
              </tr>
            </tbody>
          </table>

          <!-- Level Actions -->
          <div class="flex gap-2 mt-3">
            <button
              class="px-3 py-1.5 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
              @click="emitRepair('sync_goals', level.level)"
            >
              Sync Goals
            </button>
            <button
              class="px-3 py-1.5 text-xs bg-green-600 text-white rounded hover:bg-green-700"
              @click="emitRepair('mark_completed', level.level)"
            >
              Mark Completed
            </button>
            <button
              class="px-3 py-1.5 text-xs bg-purple-600 text-white rounded hover:bg-purple-700"
              @click="emitRepair('recalculate')"
            >
              Recalculate
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
const ANOMALY_TYPE_LABELS = {
  CURRENT_LEVEL_MISMATCH: 'Level Mismatch',
  GOAL_MISMATCH: 'Goal Mismatch',
  XP_MISMATCH: 'XP Mismatch',
  COMPLETION_MISMATCH: 'Completion Mismatch',
  MISSING_LEVEL_PROGRESS: 'Missing Levels',
  LEGACY_DATA_MISMATCH: 'Legacy Mismatch',
  GOAL_PROGRESS_BEHIND: 'Progress Behind',
};

export default {
  name: 'UserAnomalyDetail',
  props: {
    report: { type: Object, required: true },
  },
  emits: ['repair'],
  data() {
    return {
      expandedLevels: {},
    };
  },
  computed: {
    criticalAnomalies() {
      return this.report.anomalies.filter((a) => a.severity === 'critical');
    },
    nonCriticalGlobalAnomalies() {
      return this.report.anomalies.filter(
        (a) =>
          a.severity !== 'critical' &&
          (a.type === 'LEGACY_DATA_MISMATCH' ||
           (a.type === 'MISSING_LEVEL_PROGRESS' && !a.level)),
      );
    },
  },
  watch: {
    report: {
      immediate: true,
      handler(report) {
        if (!report) return;
        const levels = {};
        for (const level of report.levels) {
          if (level.anomalies.length > 0) {
            levels[level.level] = true;
          }
        }
        this.expandedLevels = levels;
      },
    },
  },
  methods: {
    formatAnomalyType(type) {
      return ANOMALY_TYPE_LABELS[type] || type;
    },

    toggleLevel(levelNum) {
      this.expandedLevels = {
        ...this.expandedLevels,
        [levelNum]: !this.expandedLevels[levelNum],
      };
    },

    emitRepair(action, levelNumber, goalId) {
      this.$emit('repair', {
        action,
        userId: this.report.userId,
        levelNumber,
        goalId,
      });
    },

    mergedGoals(level) {
      const userGoals = level.userGoals || [];
      const templateGoals = level.templateGoals || [];

      // Order-based pairing matches backend detectGoalMismatches / repair (duplicate template ids
      // cannot be represented in a Map by id alone).
      if (userGoals.length === templateGoals.length && userGoals.length > 0) {
        return userGoals.map((userGoal, i) => {
          const templateGoal = templateGoals[i];
          const idMatch = userGoal.id === templateGoal.id;
          return {
            id: idMatch ? userGoal.id : `${userGoal.id} ↔ ${templateGoal.id}`,
            title: userGoal.title ?? templateGoal.title,
            type: userGoal.type ?? templateGoal.type,
            progress: userGoal.progress,
            requiredCompletions: userGoal.requiredCompletions ?? templateGoal.requiredCompletions,
            xp: userGoal.xp ?? templateGoal.xp,
            completed: userGoal.completed,
            inTemplate: true,
            mismatch:
              !idMatch || this.hasFieldDifference(userGoal, templateGoal),
          };
        });
      }

      const templateGoalMap = new Map();
      for (const g of templateGoals) {
        templateGoalMap.set(g.id, g);
      }

      const merged = [];
      const seenIds = new Set();

      for (const userGoal of userGoals) {
        const templateGoal = templateGoalMap.get(userGoal.id);
        seenIds.add(userGoal.id);
        merged.push({
          id: userGoal.id,
          title: userGoal.title,
          type: userGoal.type,
          progress: userGoal.progress,
          requiredCompletions: userGoal.requiredCompletions,
          xp: userGoal.xp,
          completed: userGoal.completed,
          inTemplate: !!templateGoal,
          mismatch: !templateGoal || this.hasFieldDifference(userGoal, templateGoal),
        });
      }

      for (const templateGoal of templateGoals) {
        if (!seenIds.has(templateGoal.id)) {
          merged.push({
            id: templateGoal.id,
            title: templateGoal.title,
            type: templateGoal.type,
            progress: undefined,
            requiredCompletions: templateGoal.requiredCompletions,
            xp: templateGoal.xp,
            completed: undefined,
            inTemplate: true,
            mismatch: true,
          });
        }
      }

      return merged;
    },

    hasFieldDifference(userGoal, templateGoal) {
      const fields = ['xp', 'type', 'assessmentType', 'requiredCompletions'];
      return fields.some(
        (f) => JSON.stringify(userGoal[f]) !== JSON.stringify(templateGoal[f]),
      );
    },

    goalTitle(goal) {
      if (!goal.title) return '-';
      if (typeof goal.title === 'object') {
        return goal.title.en || goal.title.de || Object.values(goal.title)[0] || '-';
      }
      return goal.title;
    },

    levelCriticalCount(level) {
      return (level.anomalies || []).filter((a) => a.severity === 'critical').length;
    },

    levelErrorCount(level) {
      return (level.anomalies || []).filter((a) => a.severity === 'error' || a.severity === 'critical').length;
    },

    levelHintCount(level) {
      return (level.anomalies || []).filter((a) => a.severity === 'hint').length;
    },

    levelBorderClass(level) {
      if (this.levelCriticalCount(level) > 0) return 'anomaly-critical-level-border';
      if (this.levelErrorCount(level) > 0) return 'border-red-300';
      if (this.levelHintCount(level) > 0) return 'border-amber-300';
      return 'border-gray-200';
    },

    levelHeaderClass(level) {
      if (this.levelCriticalCount(level) > 0) return 'anomaly-critical-level-header';
      if (this.levelErrorCount(level) > 0) return 'bg-red-50';
      if (this.levelHintCount(level) > 0) return 'bg-amber-50';
      return 'bg-gray-50';
    },
  },
};
</script>

<style scoped>
.anomaly-critical-banner {
  background: linear-gradient(135deg, #fef2f2, #fee2e2);
  border: 2px solid #ef4444;
  color: #991b1b;
  animation: critical-glow 2s ease-in-out infinite;
  box-shadow: 0 0 12px rgba(239, 68, 68, 0.25);
}

.anomaly-critical-icon {
  color: #dc2626;
  animation: icon-shake 2s ease-in-out infinite;
}

.anomaly-critical-level-border {
  border: 2px solid #ef4444 !important;
  animation: critical-glow 2s ease-in-out infinite;
}

.anomaly-critical-level-header {
  background: linear-gradient(135deg, #fef2f2, #fee2e2);
}

@keyframes critical-glow {
  0%, 100% { box-shadow: 0 0 8px rgba(239, 68, 68, 0.2); }
  50% { box-shadow: 0 0 20px rgba(239, 68, 68, 0.5); }
}

@keyframes icon-shake {
  0%, 100% { transform: rotate(0deg); }
  10% { transform: rotate(12deg); }
  20% { transform: rotate(-12deg); }
  30% { transform: rotate(8deg); }
  40% { transform: rotate(-8deg); }
  50% { transform: rotate(0deg); }
}
</style>
